/**
 * Sentrifuga — dinamik rejim: cho'kma barabanda to'planadi, Σ kamayadi, ushlanish pasayadi;
 * `discharge ≥ 0.5` — baraban bo'shatiladi (oniy).
 *   dm/dt = Q·c·η(m)
 */
import { warn, type StepResult, type Warning } from '../../types';
import { evaluate, validateCentrifugeInputs, validateCentrifugeParams } from './steady';
import type { CentrifugeInputs, CentrifugeOutputs, CentrifugeParams, CentrifugeState } from './types';

export function initState(p: CentrifugeParams, i: CentrifugeInputs): CentrifugeState {
  validateCentrifugeParams(p);
  validateCentrifugeInputs(i);
  return { t: 0, solidsMass: 0 };
}

export function stepDynamic(p: CentrifugeParams, s: CentrifugeState, i: CentrifugeInputs, dt: number): StepResult<CentrifugeState, CentrifugeOutputs> {
  validateCentrifugeInputs(i);
  if (!(dt > 0)) throw new Error('centrifuge: dt must be > 0');
  const warnings: Warning[] = [];
  let m = (i.discharge ?? 0) >= 0.5 ? 0 : s.solidsMass;
  const e = evaluate(p, i, m, warnings);
  const captured = i.flowRate * i.solidsConcentration * e.recovery;
  m = Math.min(p.solidsHoldup, m + captured * dt);
  const bowlFill = m / p.solidsHoldup;
  if (e.sigma <= 0 || bowlFill >= 1) warn(warnings, 'cent.bowlFull', 'critical', { fill: Math.round(bowlFill * 100) });
  else if (e.recovery < 0.5) warn(warnings, 'cent.lowRecovery', 'warning', { recovery: Math.round(e.recovery * 100) });
  if (e.reParticle > 1) warn(warnings, 'cent.stokesInvalid', 'info', { re: Math.round(e.reParticle * 10) / 10 });
  const outputs: CentrifugeOutputs = {
    t: s.t + dt,
    omega: e.omega,
    separationFactor: e.separationFactor,
    sigma: e.sigma,
    dCut: e.dCut,
    recovery: e.recovery,
    overflowSolids: i.solidsConcentration * (1 - e.recovery),
    solidsCaptured: captured,
    power: e.power,
    solidsMass: m,
    bowlFill,
    effectiveRadius: e.effectiveRadius,
    reParticle: e.reParticle,
    timeToFill: captured > 0 ? (p.solidsHoldup - m) / captured : Infinity,
  };
  return { state: { t: s.t + dt, solidsMass: m }, outputs, warnings };
}
